import React from 'react';

/**
 * Button component aligned with MHDF Momentum redesign specifications
 * Variants: primary, secondary, ghost, clinician (outlined)
 * Sizes: sm (10px 16px), md (12px 24px), lg (16px 32px), xl (18px 40px)
 */
const Button = ({ 
  children, 
  variant = 'primary', 
  size = 'md', 
  disabled = false,
  fullWidth = false,
  onClick,
  type = 'button',
  className = '',
  icon: Icon,
  ...props 
}) => {
  const baseStyles = 'inline-flex items-center justify-center font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
  
  const variants = {
    // Primary CTA - Solid primary color, white text
    primary: 'bg-[var(--gold-accent)] text-[var(--gold-accent-text)] hover:brightness-95 focus:ring-[var(--gold-accent)] shadow-sm hover:shadow-md active:scale-[0.98]',
    
    // Secondary actions - White/transparent with primary border
    secondary: 'border-2 border-[var(--brand-green-deep)] bg-[var(--surface-card)] text-[var(--brand-green-deep)] hover:bg-[var(--surface-soft-1)] focus:ring-[var(--brand-green-deep)] active:scale-[0.98]',
    
    // Tertiary actions - Text only with hover underline
    ghost: 'text-[var(--brand-green-deep)] hover:underline hover:opacity-90 focus:ring-[var(--brand-green-deep)]',
    
    // Clinician-specific CTAs - Indigo color
    clinician: 'bg-[var(--brand-green-deep)] text-white hover:brightness-110 focus:ring-[var(--brand-green-deep)] shadow-sm hover:shadow-md active:scale-[0.98]',
    
    // Danger variant
    danger: 'bg-error text-white hover:bg-error/90 focus:ring-error shadow-sm hover:shadow-md active:scale-[0.98]',
    
    // Vibrant color variants for feature-specific actions
    purple: 'bg-purple-600 text-white hover:bg-purple-700 focus:ring-purple-500 shadow-sm hover:shadow-md active:scale-[0.98]',
    emerald: 'bg-emerald-600 text-white hover:bg-emerald-700 focus:ring-emerald-500 shadow-sm hover:shadow-md active:scale-[0.98]',
    coral: 'bg-coral-500 text-white hover:bg-coral-600 focus:ring-coral-500 shadow-sm hover:shadow-md active:scale-[0.98]',
    amber: 'bg-amber-500 text-white hover:bg-amber-600 focus:ring-amber-500 shadow-sm hover:shadow-md active:scale-[0.98]',
    cyan: 'bg-cyan-600 text-white hover:bg-cyan-700 focus:ring-cyan-500 shadow-sm hover:shadow-md active:scale-[0.98]',
    
    // Outline variants for vibrant colors
    'purple-outline': 'border-2 border-purple-600 bg-white text-purple-700 hover:bg-purple-50 focus:ring-purple-500 active:scale-[0.98]',
    'emerald-outline': 'border-2 border-emerald-600 bg-white text-emerald-700 hover:bg-emerald-50 focus:ring-emerald-500 active:scale-[0.98]',
    'coral-outline': 'border-2 border-coral-500 bg-white text-coral-600 hover:bg-coral-50 focus:ring-coral-500 active:scale-[0.98]',
    'amber-outline': 'border-2 border-amber-500 bg-white text-amber-700 hover:bg-amber-50 focus:ring-amber-500 active:scale-[0.98]',
    
    // Success/Warning/Error with vibrant colors
    success: 'bg-emerald-600 text-white hover:bg-emerald-700 focus:ring-emerald-500 shadow-sm hover:shadow-md active:scale-[0.98]',
    warning: 'bg-amber-500 text-white hover:bg-amber-600 focus:ring-amber-500 shadow-sm hover:shadow-md active:scale-[0.98]',
    destructive: 'bg-coral-600 text-white hover:bg-coral-700 focus:ring-coral-500 shadow-sm hover:shadow-md active:scale-[0.98]',
  };
  
  const sizes = {
    sm: 'px-4 py-2.5 text-sm rounded-xl',
    md: 'px-6 py-3 text-sm rounded-xl',
    lg: 'px-8 py-4 text-base rounded-2xl',
    xl: 'px-10 py-[18px] text-base rounded-2xl',
  };
  
  const widthClass = fullWidth ? 'w-full' : '';
  
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${widthClass} ${className}`}
      {...props}
    >
      {Icon && <Icon className="w-5 h-5 mr-2" />}
      {children}
    </button>
  );
};

export default Button;
